import { useEffect, useRef, useState } from 'react';

interface ScaledFigmaCanvasProps {
  baseWidth: number;
  baseHeight: number;
  ariaLabel: string;
  children: React.ReactNode;
}

const getScale = (width: number, height: number, baseWidth: number, baseHeight: number) => {
  if (!width || !height) {
    return 1;
  }
  const scaleX = width / baseWidth;
  const scaleY = height / baseHeight;
  return Math.min(scaleX, scaleY);
};

const ScaledFigmaCanvas: React.FC<ScaledFigmaCanvasProps> = ({ baseWidth, baseHeight, ariaLabel, children }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const updateScale = () => {
      const { clientWidth, clientHeight } = container;
      setScale((previous) => {
        const next = getScale(clientWidth, clientHeight, baseWidth, baseHeight);
        return Math.abs(previous - next) < 0.001 ? previous : next;
      });
    };

    updateScale();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [baseWidth, baseHeight]);

  const scaledWidth = baseWidth * scale;
  const scaledHeight = baseHeight * scale;

  return (
    <div ref={containerRef} className="figma-accueil-canvas" role="img" aria-label={ariaLabel}>
      <div className="figma-accueil-wrapper" style={{ width: scaledWidth, height: scaledHeight }}>
        <div
          className="figma-accueil-content"
          style={{ width: baseWidth, height: baseHeight, transform: `scale(${scale})` }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default ScaledFigmaCanvas;
